import { Component, type ErrorInfo, type ReactNode } from 'react';
import {
  Button,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

interface Props {
  children: ReactNode;
}

/**
 * Top-level error boundary. Without this, any unhandled render crash (bad
 * prop, undefined component, etc.) leaves the user on a completely blank
 * page with no feedback. Catching it here at least surfaces the stack so
 * the user can report or reload.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
    this.setState({ error, info });
  }

  private handleReload = () => {
    this.setState({ error: null, info: null });
    window.location.reload();
  };

  override render() {
    if (this.state.error) {
      return <ErrorView error={this.state.error} info={this.state.info} onReload={this.handleReload} />;
    }
    return this.props.children;
  }
}

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    padding: '32px',
    background: tokens.colorNeutralBackground2,
    display: 'grid',
    placeItems: 'start center',
  },
  card: {
    background: tokens.colorNeutralBackground1,
    padding: '32px',
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow28,
    maxWidth: '720px',
    width: '100%',
    display: 'grid',
    gap: '16px',
  },
  pre: {
    margin: 0,
    padding: '12px',
    background: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    overflow: 'auto',
    maxHeight: '320px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
});

function ErrorView({
  error,
  info,
  onReload,
}: {
  error: Error;
  info: ErrorInfo | null;
  onReload: () => void;
}) {
  const styles = useStyles();
  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Something went wrong</MessageBarTitle>
            {error.message}
          </MessageBarBody>
        </MessageBar>
        <pre className={styles.pre}>{error.stack ?? error.message}</pre>
        {info?.componentStack ? (
          <pre className={styles.pre}>{info.componentStack}</pre>
        ) : null}
        <div>
          <Button appearance="primary" onClick={onReload}>
            Reload
          </Button>
        </div>
      </div>
    </div>
  );
}
